/**
 * UserMenu Component
 * Avatar popover menu with user profile and navigation options
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Sparkles, UserCog, Settings, HelpCircle, LogOut } from 'lucide-react';
import { Avatar } from '../../ui';
import { useAuth } from '../../../context';
import styles from './UserMenu.module.css';

export interface UserMenuProps {
  user: {
    name: string;
    username?: string;
    avatar?: string;
  };
}

export const UserMenu: React.FC<UserMenuProps> = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const navigate = useNavigate();
  const { logout } = useAuth();

  /** Compute popover position anchored to the avatar trigger */
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPopoverStyle({
      position: 'fixed',
      bottom: window.innerHeight - rect.bottom,
      left: rect.right + 12,
    });
  }, []);

  const toggleMenu = () => {
    if (!isOpen) updatePosition();
    setIsOpen(!isOpen);
  };

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node;
    if (
      menuRef.current && !menuRef.current.contains(target) &&
      popoverRef.current && !popoverRef.current.contains(target)
    ) {
      setIsOpen(false);
    }
  }, []);

  const handleEscape = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('resize', updatePosition);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, handleClickOutside, handleEscape, updatePosition]);

  const handleNavigate = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    setIsOpen(false);
    logout();
  };

  return (
    <div className={styles.userMenu} ref={menuRef}>
      <button
        type="button"
        ref={triggerRef}
        className={styles.avatarButton}
        onClick={toggleMenu}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Avatar src={user.avatar} name={user.name} size="sm" />
      </button>

      {isOpen && createPortal(
        <div className={styles.popover} ref={popoverRef} style={popoverStyle}>
          <div className={styles.userHeader}>
            <Avatar src={user.avatar} name={user.name} size="md" />
            <div className={styles.userInfo}>
              <div className={styles.userName}>{user.name}</div>
              {user.username && (
                <div className={styles.userHandle}>@{user.username}</div>
              )}
            </div>
          </div>

          <div className={styles.divider} />

          <nav className={styles.menuItems}>
            <button
              className={styles.menuItem}
              onClick={() => handleNavigate('/upgrade')}
            >
              <Sparkles size={16} />
              <span>Upgrade Plan</span>
            </button>

            <button
              className={styles.menuItem}
              onClick={() => handleNavigate('/settings/personalization')}
            >
              <UserCog size={16} />
              <span>Personalization</span>
            </button>

            <button
              className={styles.menuItem}
              onClick={() => handleNavigate('/settings')}
            >
              <Settings size={16} />
              <span>Settings</span>
            </button>

            <button
              className={styles.menuItem}
              onClick={() => handleNavigate('/help')}
            >
              <HelpCircle size={16} />
              <span>Help</span>
            </button>

            <div className={styles.divider} />

            <button className={styles.menuItem} onClick={handleLogout}>
              <LogOut size={16} />
              <span>Log Out</span>
            </button>
          </nav>
        </div>,
        document.body
      )}
    </div>
  );
};

export default UserMenu;
